import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import BarChartIcon from "@mui/icons-material/BarChart";
import { BarChart } from "@mui/x-charts/BarChart";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EnqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import {
  CustomerRetentionCategory,
  CustomerRetentionTotals,
  CustomerRetentionTrendMonth,
  GetCustomerRetentionResponse,
} from "subModule/src/services/analytics/interface";
import { useGetCustomerRetentionService } from "subModule/src/services/analytics/useAnalytics";
import {
  dashboardWidgetHeaderSx,
  dashboardWidgetFiltersSx,
  dashboardWidgetTitleSx,
  dashboardWidgetIconSx,
  dashboardWidgetTitleRowSx,
} from "components/Analytics/dashboardWidgetLayout";

interface CustomerRetentionTrendProps {
  themes: any;
  enqueueSnackbar: EnqueueSnackbar;
  globalFilters: {
    stationId: any;
    range: any;
    allStation: boolean;
    businessId?: any;
  };
  globalFilterApplied: boolean;
  setGlobalFilterApplied: (value: boolean) => void;
  stationsLoaded: boolean;
}

const CATEGORY_META: {
  key: CustomerRetentionCategory;
  color: string;
  labelKey: string;
}[] = [
  { key: "active", color: "#2F80ED", labelKey: "retentionActive" },
  { key: "followUp", color: "#F2C94C", labelKey: "retentionFollowUp" },
  { key: "atRisk", color: "#E67E22", labelKey: "retentionAtRisk" },
  { key: "lost", color: "#EB5757", labelKey: "retentionLost" },
];

const emptyTotals = (): CustomerRetentionTotals => ({
  active: 0,
  followUp: 0,
  atRisk: 0,
  lost: 0,
});

export default function CustomerRetentionTrend(
  props: CustomerRetentionTrendProps,
) {
  const { t } = useTranslation();
  const firstRender = useRef(true);
  const isLargeScreen = useMediaQuery(`@media('min-width: 1200px')`);
  const [totals, setTotals] = useState<CustomerRetentionTotals>(emptyTotals());
  const [trend, setTrend] = useState<CustomerRetentionTrendMonth[]>([]);
  const [loading, setLoading] = useState(true);

  const businessId =
    props.globalFilters.businessId?.value ??
    props.globalFilters.businessId ??
    null;

  const { refetch: apiRefetch } = useGetCustomerRetentionService({
    stationId: props.globalFilters.stationId?.value || null,
    range: props.globalFilters.range?.value,
    allStation: props.globalFilters.allStation,
    ...(businessId ? { businessId: Number(businessId) } : {}),
  });

  const fetchData = async () => {
    setLoading(true);
    await apiRefetch()
      .then((res: GetCustomerRetentionResponse | unknown | any) => {
        if (res?.data?.success) {
          const payload = res?.data?.data || {};
          setTotals(payload.totals || emptyTotals());
          setTrend(payload.trend || []);
        } else {
          props.enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => {
        setLoading(false);
        if (props.globalFilterApplied) props.setGlobalFilterApplied(false);
      });
  };

  useEffect(() => {
    if (firstRender.current && props.stationsLoaded) {
      firstRender.current = false;
      setTimeout(() => fetchData(), 50);
    }
  }, [props.stationsLoaded]);

  useEffect(() => {
    if (props.globalFilterApplied) {
      setTimeout(() => fetchData(), 100);
    }
  }, [props.globalFilterApplied]);

  const chartData = useMemo(() => {
    const months = trend.map((item) => item.month);
    return {
      months,
      series: CATEGORY_META.map((category) => ({
        id: category.key,
        label: t(category.labelKey),
        data: trend.map((item) => Number(item[category.key] || 0)),
        stack: "retention",
        color: category.color,
      })),
    };
  }, [trend, t]);

  const hasData = trend.some(
    (item) =>
      (item.active || 0) +
        (item.followUp || 0) +
        (item.atRisk || 0) +
        (item.lost || 0) >
      0,
  );

  return (
    <Card
      sx={{
        boxShadow: "none",
        borderRadius: "8px",
        width: "100%",
        height: "100%",
        maxHeight: { xs: 360, md: 340 },
        display: "flex",
        flexDirection: "column",
        border: "1px solid #E5E7EB",
        overflow: "hidden",
        "& .MuiCardContent-root": {
          padding: "12px !important",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
          boxSizing: "border-box",
        },
      }}
    >
      <CardContent>
        <Box sx={{ ...dashboardWidgetHeaderSx, mb: 2.5, flexShrink: 0 }}>
          <Box sx={dashboardWidgetTitleRowSx}>
            <BarChartIcon
              sx={{
                ...dashboardWidgetIconSx,
                display: { xs: "none", xl: "inline-flex" },
                fontSize: 21,
                color: props.themes?.primary || "#2563EB",
                flexShrink: 0,
              }}
            />
            <Typography
              className="font-Saira text-left"
              sx={{
                fontSize: { xs: 12, sm: 15 },
                fontWeight: { xs: 600, sm: 700 },
                textAlign: "left",
                color: "#0F2744",
                lineHeight: 1.2,
              }}
            >
              {t("customerRetentionTrend")}
            </Typography>
          </Box>
        </Box>
        {loading ? (
          <Box>
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary, mb: 1 }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary, mb: 1 }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary, mb: 1 }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
          </Box>
        ) : !hasData ? (
          <Box className="flex justify-center flex-col py-6">
            <Typography
              className="font-Saira text-center italic font-semibold"
              sx={{ color: props.themes?.primary }}
            >
              {t("noDataAvailable")}
            </Typography>
          </Box>
        ) : (
          <>
            <Box className="flex flex-wrap gap-2 mb-2" sx={{ flexShrink: 0 }}>
              {CATEGORY_META.map((category) => (
                <Box key={category.key} sx={dashboardWidgetTitleRowSx}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "2px",
                      backgroundColor: category.color,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    className="font-Saira text-left"
                    sx={{ fontSize: 11, color: "#4B5563", fontWeight: 500 }}
                  >
                    {t(category.labelKey)}
                  </Typography>
                  <Tooltip
                    title={Number(totals[category.key] || 0).toLocaleString()}
                    placement="top"
                    enterDelay={0}
                  >
                    <Typography
                      className="font-Saira text-left"
                      sx={{
                        fontSize: 11,
                        color: "#1A1A1A",
                        fontWeight: 700,
                        ml: 0.25,
                      }}
                    >
                      ({Number(totals[category.key] || 0).toLocaleString()})
                    </Typography>
                  </Tooltip>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                width: "100%",
                overflow: "auto",
                flex: 1,
                minHeight: 0,
                display: "flex",
                alignItems: "stretch",
                "&::-webkit-scrollbar": { width: 6, height: 6 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#D1D5DB",
                  borderRadius: 8,
                },
              }}
            >
              <Box
                sx={{
                  minWidth: Math.max(360, chartData.months.length * 72),
                  width: "100%",
                  flex: 1,
                  minHeight: 220,
                }}
              >
                <BarChart
                  height={220}
                  borderRadius={6}
                  xAxis={
                    [
                      {
                        data: chartData.months,
                        scaleType: "band",
                        categoryGapRatio: 0.62,
                        barGapRatio: 0.2,
                        tickLabelStyle: {
                          fontSize: 10,
                          fill: "#9CA3AF",
                          fontFamily: "Saira, sans-serif",
                        },
                      },
                    ] as any
                  }
                  series={chartData.series}
                  yAxis={[
                    {
                      valueFormatter: (val) =>
                        new Intl.NumberFormat("en", {
                          notation: "compact",
                          compactDisplay: "short",
                          maximumFractionDigits: 1,
                        }).format(Number(val)),
                      tickLabelStyle: {
                        fontSize: 10,
                        fill: "#9CA3AF",
                        fontFamily: "Saira, sans-serif",
                      },
                    },
                  ]}
                  margin={{
                    left: 40,
                    right: isLargeScreen ? 10 : 10,
                    top: 16,
                    bottom: 32,
                  }}
                  grid={{ horizontal: true }}
                  slotProps={{
                    legend: { hidden: true },
                    noDataOverlay: {
                      message: t("noRecordFound"),
                    },
                  }}
                  sx={{
                    "& .MuiBarElement-root": {
                      stroke: "#FFFFFF",
                      strokeWidth: 1.5,
                    },
                    "& .MuiChartsGrid-line": {
                      strokeDasharray: "4 4",
                      stroke: "#E5E7EB",
                    },
                    "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": {
                      stroke: "transparent",
                    },
                  }}
                />
              </Box>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}
